import { AgreementLinks } from "@/components/common/AgreementLinks";
import { Logo } from "@/components/common/Logo";
import { registerWithEmail, sendEmailCode } from "@/services/api/auth";
import {
  EyeInvisibleOutlined,
  EyeOutlined,
  LockOutlined,
  MailOutlined,
} from "@ant-design/icons";
import { App, Button, Form, Input } from "antd";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import "./Register.less";

const Register: React.FC = () => {
  const { t } = useTranslation();
  const { message: messageApi } = App.useApp();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [agree, setAgree] = useState(false);
  const [codeValues, setCodeValues] = useState<string[]>([
    "",
    "",
    "",
    "",
    "",
    "",
  ]);
  const [sendingCode, setSendingCode] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [emailValid, setEmailValid] = useState(false);
  const inputRefs = useRef<any[]>([]);

  // 倒计时
  React.useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // 监听邮箱字段变化,验证邮箱格式
  const handleFieldsChange = () => {
    const email = form.getFieldValue("email");
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setEmailValid(emailRegex.test(email));
  };

  const handleSendCode = async () => {
    try {
      await form.validateFields(["email"]);
      const email = form.getFieldValue("email");

      setSendingCode(true);
      const res = await sendEmailCode({ email });

      if (res.data.code === 200) {
        messageApi.success(t("auth.common.message_code_sent"));
        setCountdown(60);
      } else {
        messageApi.error(
          res.data.message || t("auth.common.message_send_code_failed")
        );
      }
    } catch (error: any) {
      messageApi.error(
        error?.message || t("auth.common.message_send_code_failed")
      );
    } finally {
      setSendingCode(false);
    }
  };

  const handleCodeChange = (index: number, value: string) => {
    // 只允许数字，过滤掉非数字字符
    const numericValue = value.replace(/\D/g, "").slice(0, 1);

    const newValues = [...codeValues];
    newValues[index] = numericValue;
    setCodeValues(newValues);

    // 自动聚焦到下一个输入框
    if (numericValue && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // 更新表单值
    const codeString = newValues.join("");
    form.setFieldsValue({ code: codeString });

    // 清除验证错误状态，让用户继续输入
    if (codeString.length < 6) {
      form.setFields([
        {
          name: "code",
          errors: [],
        },
      ]);
    }
  };

  const handleCodeKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Backspace" && !codeValues[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 6);
    if (/^\d+$/.test(pastedData)) {
      const newValues = pastedData
        .split("")
        .concat(Array(6 - pastedData.length).fill(""));
      setCodeValues(newValues);
      form.setFieldValue("code", pastedData);
      // 聚焦到最后一个输入框
      const nextIndex = Math.min(pastedData.length, 3);
      inputRefs.current[nextIndex]?.focus();
    }
  };

  const handleSubmit = async (values: {
    email: string;
    code: string;
    password: string;
  }) => {
    if (!agree) {
      messageApi.warning(t("auth.common.validation_agree_required"));
      return;
    }

    try {
      setLoading(true);
      const payload = {
        email: values.email,
        email_code: codeValues.join(""),
        password: values.password,
      };

      const res = await registerWithEmail(payload);

      if (res.data.code === 200) {
        messageApi.success(t("auth.common.message_register_success"));
        // 跳转到登录页面
        navigate("/auth/login");
      } else {
        messageApi.error(
          res.data.message || t("auth.common.message_register_failed")
        );
      }
    } catch (error: any) {
      messageApi.error(
        error?.message || t("auth.common.message_register_failed")
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSocialRegister = (provider: "google" | "microsoft") => {
    const providerName = provider === "google" ? "Google" : "Microsoft";
    messageApi.info(
      t("auth.common.message_social_register_coming_soon", {
        provider: providerName,
      })
    );
    // TODO: 实现社交注册
  };

  return (
    <>
      {/* Logo 和标题 */}
      <div className="register-header">
        <div className="register-logo">
          <Logo height={50} />
        </div>
        <div className="register-tagline">{t("auth.common.tagline")}</div>
      </div>

      {/* 欢迎信息 */}
      {/* <div className="register-welcome">
        <h1 className="welcome-title">{t("auth.register.welcome_title")}</h1>
        <p className="welcome-subtitle">
          {t("auth.register.welcome_subtitle")}
        </p>
      </div> */}

      {/* 注册表单 */}
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        className="register-form"
        size="large"
        onFieldsChange={handleFieldsChange}
      >
        {/* 企业邮箱输入 */}
        <Form.Item
          name="email"
          label={t("auth.common.label_corporate_email")}
          rules={[
            {
              required: true,
              message: t("auth.common.validation_email_required"),
            },
            {
              type: "email",
              message: t("auth.common.validation_email_invalid"),
            },
          ]}
        >
          <Input
            prefix={<MailOutlined className="input-icon" />}
            placeholder={t("auth.common.placeholder_email")}
            className="register-input"
          />
        </Form.Item>

        {/* 验证码输入 */}
        <Form.Item
          name="code"
          label={
            <div className="code-label">
              <span>{t("auth.common.label_verification_code")}</span>
              <Button
                type="link"
                className="get-code-button"
                onClick={handleSendCode}
                loading={sendingCode}
                disabled={!emailValid || countdown > 0}
              >
                {countdown > 0
                  ? `${countdown}s`
                  : t("auth.common.get_verification_code")}
              </Button>
            </div>
          }
          rules={[
            {
              validator: () => {
                const fullCode = codeValues.join("");
                if (!fullCode || fullCode.length === 0) {
                  return Promise.reject(
                    new Error(t("auth.common.validation_code_required"))
                  );
                }
                if (fullCode.length !== 6 || !/^\d{6}$/.test(fullCode)) {
                  return Promise.reject(
                    new Error(t("auth.common.validation_code_length_6"))
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <div className="code-inputs" onPaste={handlePaste}>
            {codeValues.map((value, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                value={value}
                maxLength={1}
                className="code-input"
                type="tel"
                pattern="[0-9]*"
                inputMode="numeric"
                onChange={(e) => handleCodeChange(index, e.target.value)}
                onKeyDown={(e) => handleCodeKeyDown(index, e)}
              />
            ))}
          </div>
        </Form.Item>

        {/* 密码输入 */}
        <Form.Item
          name="password"
          label={t("auth.common.label_password")}
          rules={[
            {
              required: true,
              message: t("auth.common.validation_password_required"),
            },
            { min: 6, message: t("auth.common.validation_password_min") },
          ]}
        >
          <Input.Password
            prefix={<LockOutlined className="input-icon" />}
            placeholder={t("auth.common.placeholder_password")}
            className="register-input"
            iconRender={(visible) =>
              visible ? (
                <EyeOutlined className="password-toggle" />
              ) : (
                <EyeInvisibleOutlined className="password-toggle" />
              )
            }
          />
        </Form.Item>

        {/* 条款复选框 */}
        <Form.Item className="agreement-item">
          <div className="agreement-wrapper">
            <AgreementLinks
              showCheckbox={true}
              checked={agree}
              onCheckboxChange={setAgree}
              prefixText={t("auth.common.agree_prefix")}
              colors={{
                primary: "#21C4B8",
                primaryDark: "#4BA67A",
              }}
            />
          </div>
        </Form.Item>

        {/* 注册按钮 */}
        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            className="register-button"
            block
          >
            {t("auth.register.btn_register")}
          </Button>
        </Form.Item>
      </Form>

      {/* 登录链接 */}
      <div className="login-link">
        {t("auth.register.login_link_prefix")}{" "}
        <Link to="/auth/login">{t("auth.register.login_link")}</Link>
      </div>

      {/* 社交登录分隔符 */}
      {/* <div className="social-divider">
        <span>{t("auth.common.social_divider")}</span>
      </div> */}

      {/* 社交登录按钮 */}
      {/* <div className="social-login">
        <Button
          className="social-button google-button"
          onClick={() => handleSocialRegister("google")}
        >
          <img src={googleIcon} alt="Google" className="social-icon" />
          <span>{t("auth.common.google")}</span>
        </Button>
        <Button
          className="social-button microsoft-button"
          onClick={() => handleSocialRegister("microsoft")}
        >
          <img src={microsoftIcon} alt="Microsoft" className="social-icon" />
          <span>{t("auth.common.microsoft")}</span>
        </Button>
      </div> */}
    </>
  );
};

export default Register;
